import { Trans } from '@lingui/macro'
import Circle from 'assets/images/white-loader.svg'
import BigNumber from 'bignumber.js'
import { ButtonPrimary, ButtonSecondary } from 'components/Button'
import { ColumnCenter } from 'components/Column'
import Modal from 'components/Modal'
import { RowBetween, RowCenter } from 'components/Row'
import { useSportContract } from 'hooks/useContract'
import usePlayerData from 'hooks/usePlayerData'
import { useEffect, useState } from 'react'
import { AlertTriangle } from 'react-feather'
import NumberFormat from 'react-number-format'
import { useDarkModeManager } from 'state/user/hooks'
import styled from 'styled-components/macro'
import { CustomLightSpinner, ThemedText } from 'theme'
import { DEFAULT_TOKEN_DECIMALS } from 'utils/bigNumber'
import { tokenToDecimals } from 'utils/tokens'

import * as StyleSport from '../style'
import { calculateRatio } from './Events'
import { ESportType } from './Filter'

const TeamSelect = styled.div<{ isSelected?: boolean }>`
  width: 30%;
  text-align: center;
  border: 1px solid ${({ theme, isSelected }) => (isSelected ? theme.primary1 : theme.primary5)};
  border-radius: 15px;
  padding: 5px;
  &:hover {
    cursor: pointer;
  }
`

const Container = styled.div`
  width: 100%;
  padding: 24px;
`

const InfoData = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  padding: 8px;
  background: ${({ theme }) => theme.bg2};
  border-radius: 8px;
`

const Message = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  padding: 8px;
  border: 1px solid #ff8f00;
  border-radius: 8px;
`

interface ISportProvider {
  id: string
  leagueName: string
  league: string
  sportType: string
}

interface IAcornSportBets {
  player: string
}

interface IAcornSportEvents {
  id: string
  gameIndex: string
  homeTeam: string
  homeScore?: number
  awayTeam: string
  awayScore?: number
  statusId?: number
  startTime: string
  volumen?: string
  volumenHome: string
  volumenAway: string
  volumenDraw: string
  provider: ISportProvider
  bets?: IAcornSportBets[]
}

export const SportBetModal = ({
  isOpen,
  titleEvent,
  dateEvent,
  homeTeam,
  awayTeam,
  itemEvent,
  onDismiss,
  onTeamSelect,
}: {
  isOpen: boolean
  titleEvent: string
  dateEvent: string
  homeTeam: string
  awayTeam: string
  itemEvent?: IAcornSportEvents
  onDismiss: () => void
  onTeamSelect?: number
}) => {
  const [isDark] = useDarkModeManager()
  const [teamSelected, setTeamSeleted] = useState<number | undefined>()
  const playerData = usePlayerData()

  const [betAmount, setBetAmount] = useState(0)
  const [minBetAmount, setMinBetAmount] = useState(500)

  const BigBetAmount = new BigNumber(betAmount).times(DEFAULT_TOKEN_DECIMALS)
  const haveFunds = playerData.chipsBalance.gte(BigBetAmount)

  const sportContract = useSportContract()

  const setTeamBet = (team: number) => {
    setTeamSeleted(team)
  }

  const handleFocus = (event: { target: { select: () => any } }) => event.target.select()

  useEffect(() => {
    if (isOpen && onTeamSelect === 999) setTeamSeleted(undefined)
    if (isOpen && [0, 1, 2].includes(Number(onTeamSelect))) setTeamSeleted(onTeamSelect)
  }, [onTeamSelect, isOpen])

  const [loadingPlayBet, setLoadingPlayBet] = useState(false)
  const [attempting, setAttempting] = useState(false)

  useEffect(() => {
    const tempMinValue = 5000
    setMinBetAmount(tempMinValue)
    setBetAmount(tempMinValue)
  }, [])

  const betInEvent = async () => {
    setAttempting(true)
    setLoadingPlayBet(true)
    try {
      if (betAmount < minBetAmount) {
        throw new Error('Error invalid bet amount')
      }

      const response = await sportContract?.betInGame(
        BigBetAmount.toFixed(0),
        itemEvent?.provider.id || '',
        itemEvent?.gameIndex || '',
        teamSelected || 0
      )

      await response?.wait()
      onDismiss()
      setAttempting(false)
      setLoadingPlayBet(false)
    } catch (error) {
      console.log(error)
      setAttempting(false)
      setLoadingPlayBet(false)
    }
  }

  const calculateOnAmount = (currentVolumen: string, team: number) => {
    const currentVolumenInNumber = tokenToDecimals(currentVolumen, 18)
    const betPerTeam = teamSelected === team ? betAmount : 0
    const calculateValue = new BigNumber(betPerTeam + currentVolumenInNumber).times(DEFAULT_TOKEN_DECIMALS).toFixed(0)
    const currentTotalVolumenInNumber = tokenToDecimals(itemEvent?.volumen || '0', 18)
    const currentTotalVolumen = new BigNumber(betAmount + currentTotalVolumenInNumber)
      .times(DEFAULT_TOKEN_DECIMALS)
      .toFixed(0)
    return calculateRatio(currentTotalVolumen, calculateValue)
  }

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss}>
      <Container>
        <RowCenter>
          <img
            width="128px"
            src={
              isDark
                ? `${process.env.PUBLIC_URL}/images/sports/leagues/${titleEvent}_White.png`
                : `${process.env.PUBLIC_URL}/images/sports/leagues/${titleEvent}.png`
            }
            alt=""
          />
        </RowCenter>
        <RowCenter mt="1rem">{new Date(Number(dateEvent) * 1000).toLocaleString()}</RowCenter>
        <InfoData>
          <ColumnCenter>
            <NumberFormat
              value={tokenToDecimals(itemEvent?.volumenHome || '0', 18)}
              decimalScale={2}
              thousandSeparator
              fixedDecimalScale
              displayType="text"
            />
            <NumberFormat
              value={calculateRatio(itemEvent?.volumen || '0', itemEvent?.volumenHome || '0')}
              suffix="x"
              decimalScale={2}
              fixedDecimalScale
              displayType="text"
            />
          </ColumnCenter>
          {ESportType.SOCCER === Number(itemEvent?.provider.league) && (
            <ColumnCenter>
              <NumberFormat
                value={tokenToDecimals(itemEvent?.volumenDraw || '0', 18)}
                decimalScale={2}
                thousandSeparator
                fixedDecimalScale
                displayType="text"
              />
              <NumberFormat
                value={calculateRatio(itemEvent?.volumen || '0', itemEvent?.volumenDraw || '0')}
                suffix="x"
                decimalScale={2}
                fixedDecimalScale
                displayType="text"
              />
            </ColumnCenter>
          )}
          <ColumnCenter>
            <NumberFormat
              value={tokenToDecimals(itemEvent?.volumenAway || '0', 18)}
              decimalScale={2}
              thousandSeparator
              fixedDecimalScale
              displayType="text"
            />
            <NumberFormat
              value={calculateRatio(itemEvent?.volumen || '0', itemEvent?.volumenAway || '0')}
              suffix="x"
              decimalScale={2}
              fixedDecimalScale
              displayType="text"
            />
          </ColumnCenter>
        </InfoData>
        <RowBetween mt="1rem">
          <ColumnCenter>
            <img
              src={`${process.env.PUBLIC_URL}/images/sports/tshirts/${titleEvent}/${homeTeam}_Home.svg`}
              alt="home"
              width="64px"
            />
            <ThemedText.Body textAlign="center">{itemEvent?.homeTeam}</ThemedText.Body>
          </ColumnCenter>
          <ColumnCenter>
            <ThemedText.Body textAlign="center">VS</ThemedText.Body>
          </ColumnCenter>
          <ColumnCenter>
            <img
              src={`${process.env.PUBLIC_URL}/images/sports/tshirts/${titleEvent}/${awayTeam}_Away.svg`}
              alt="home"
              width="64px"
            />
            <ThemedText.Body textAlign="center">{itemEvent?.awayTeam}</ThemedText.Body>
          </ColumnCenter>
        </RowBetween>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '1rem',
          }}
        >
          <TeamSelect isSelected={teamSelected === 0} onClick={() => setTeamBet(0)}>
            <NumberFormat
              value={calculateOnAmount(itemEvent?.volumenHome || '0', 0)}
              suffix="x"
              decimalScale={2}
              fixedDecimalScale
              displayType="text"
            />
          </TeamSelect>

          {Number(itemEvent?.provider.league) === ESportType.SOCCER ? (
            <TeamSelect isSelected={teamSelected === 2} onClick={() => setTeamBet(2)}>
              <NumberFormat
                value={calculateOnAmount(itemEvent?.volumenDraw || '0', 2)}
                suffix="x"
                decimalScale={2}
                fixedDecimalScale
                displayType="text"
              />
            </TeamSelect>
          ) : (
            <div
              style={{
                width: '20%',
                textAlign: 'center',
                border: '1px solid #567',
                borderRadius: '15px',
                padding: '5px',
              }}
            >
              X
            </div>
          )}

          <TeamSelect isSelected={teamSelected === 1} onClick={() => setTeamBet(1)}>
            <NumberFormat
              value={calculateOnAmount(itemEvent?.volumenAway || '0', 1)}
              suffix="x"
              decimalScale={2}
              fixedDecimalScale
              displayType="text"
            />
          </TeamSelect>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <StyleSport.ModalBetInputNumber
            thousandSeparator
            decimalScale={4}
            value={betAmount}
            onFocus={handleFocus}
            placeholder={'Introduce cantidad'}
            disabled={loadingPlayBet || attempting}
            onValueChange={(values) => {
              const { value } = values
              setBetAmount(Number(value))
            }}
            onBlur={() => {
              if (betAmount < minBetAmount) {
                setBetAmount(minBetAmount)
              }
            }}
          />
        </div>

        <Message>
          <AlertTriangle color="#FF8F00" />
          <ThemedText.Body color="#FF8F00" ml="4px">
            <Trans>Bets cannot be withdrawn</Trans>
          </ThemedText.Body>
        </Message>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            margin: 'auto',
            justifyContent: 'center',
            width: '100%',
            marginTop: '1rem',
          }}
        >
          <ButtonPrimary onClick={betInEvent} disabled={!haveFunds || betAmount === 0 || loadingPlayBet || attempting}>
            <Trans>Bet</Trans>
            {loadingPlayBet ? (
              <CustomLightSpinner
                src={Circle}
                alt="loader"
                size={'18px'}
                style={{ marginLeft: '0.5em', textDecoration: 'bold' }}
              />
            ) : null}
          </ButtonPrimary>
          <div style={{ width: '30px', height: 10 }}></div>
          <ButtonSecondary onClick={onDismiss}>
            <Trans>Exit</Trans>
          </ButtonSecondary>
        </div>
      </Container>
    </Modal>
  )
}
